import { Action, createReducer, on } from '@ngrx/store';
import * as DatasetsActions from '../actions';
import { IDatasetFile } from '../../models/Dataset';
import { IAnalyticalDataset } from '../../models/AnalyticalDataset';

export interface DatasetsState {
  datasetFiles: IDatasetFile[] | null;
  selectDatasetId: number | null;
  selectDataset: IDatasetFile | null;
  // Analytical Dataset state
  analyticalDatasets: IAnalyticalDataset[];
  analyticalDatasetsLoading: boolean;
  selectedAnalyticalDataset: IAnalyticalDataset | null;
  isDeleting: boolean;
}

const initialState: DatasetsState = {
  datasetFiles: [],
  selectDatasetId: null,
  selectDataset: null,
  // Analytical Dataset state
  analyticalDatasets: [],
  analyticalDatasetsLoading: false,
  selectedAnalyticalDataset: null,
  isDeleting: false,
};

export const datasetsReducer = createReducer(
  initialState,

  on(DatasetsActions.DatasetsUiActions.GetDatasetFiles, (state) => ({
    ...state,
  })),
  on(DatasetsActions.DatasetsApiActions.getDatasetFilesSuccess, (state, { payload }) => ({
    ...state,
    datasetFiles: payload,
  })),
  on(DatasetsActions.DatasetsApiActions.getDatasetFilesError, (state) => ({
    ...state,
    datasetFiles: [],
  })),
  on(DatasetsActions.DatasetsUiActions.SelectDatasetId, (state, { datasetId }) => ({
    ...state,
    selectDatasetId: datasetId,
  })),
  on(DatasetsActions.DatasetsUiActions.ClearDatasetId, (state) => ({
    ...state,
    selectDatasetId: null,
  })),
  on(DatasetsActions.DatasetsUiActions.PatchDataset, (state) => ({
    ...state,
  })),
  on(DatasetsActions.DatasetsApiActions.patchDatasetSuccess, (state) => ({
    ...state,
  })),
  on(DatasetsActions.DatasetsApiActions.patchDatasetError, (state) => ({
    ...state,
  })),
  on(DatasetsActions.DatasetsUiActions.SelectDataset, (state, { dataset }) => ({
    ...state,
    selectDataset: dataset,
  })),
  on(DatasetsActions.DatasetsUiActions.PostGrantDatasetAccess, (state) => ({
    ...state,
  })),
  on(DatasetsActions.DatasetsApiActions.postGrantDatasetAccessSuccess, (state) => ({
    ...state,
  })),
  on(DatasetsActions.DatasetsApiActions.postGrantDatasetAccessError, (state) => ({
    ...state,
  })),
  on(DatasetsActions.DatasetsUiActions.GetDatasetById, (state) => ({
    ...state,
  })),
  on(DatasetsActions.DatasetsApiActions.getAndSelectDatasetSuccess, (state, { payload }) => ({
    ...state,
    selectDataset: payload,
  })),
  on(DatasetsActions.DatasetsApiActions.getAndSelectDatasetError, (state) => ({
    ...state,
    selectDataset: null,
  })),
  // Analytical Dataset reducers
  on(DatasetsActions.DatasetsUiActions.GetAnalyticalDatasets, (state) => ({
    ...state,
    analyticalDatasetsLoading: true,
  })),
  on(DatasetsActions.DatasetsApiActions.getAnalyticalDatasetsSuccess, (state, { payload }) => ({
    ...state,
    analyticalDatasets: payload,
    analyticalDatasetsLoading: false,
  })),
  on(DatasetsActions.DatasetsApiActions.getAnalyticalDatasetsError, (state) => ({
    ...state,
    analyticalDatasetsLoading: false,
  })),
  on(DatasetsActions.DatasetsUiActions.SelectAnalyticalDataset, (state, { dataset }) => ({
    ...state,
    selectedAnalyticalDataset: dataset,
  })),
  on(DatasetsActions.DatasetsUiActions.DeleteAnalyticalDataset, (state) => ({
    ...state,
    isDeleting: true,
  })),
  on(DatasetsActions.DatasetsApiActions.deleteAnalyticalDatasetSuccess, (state, { datasetId }) => ({
    ...state,
    isDeleting: false,
    selectedAnalyticalDataset: null,
    // Deletion is async (202). Keep the dataset in the list but flag it as
    // DELETING so the UI can show a badge and disable actions; the row is
    // removed on the next list refresh once teardown completes server-side.
    // Guard against a missing array: ngrx-store-localstorage rehydrates this
    // slice from localStorage on boot and can leave `analyticalDatasets`
    // undefined. Throwing here would abort the action dispatch *inside* the
    // delete HTTP response's synchronous emission, so the request observable
    // never completes and the loader interceptor's finalize never runs —
    // stranding the global spinner at loadingCount 1.
    analyticalDatasets: (state.analyticalDatasets ?? []).map((d) =>
      d.id === datasetId ? { ...d, deletion_status: 'DELETING' as const } : d
    ),
  })),
  on(DatasetsActions.DatasetsApiActions.deleteAnalyticalDatasetError, (state) => ({
    ...state,
    isDeleting: false,
  }))
);

export function DatasetsReducer(state: DatasetsState | undefined, action: Action) {
  return datasetsReducer(state, action);
}
